// -- Lifecycle hooks
if (!customElements.get('momax-journal')) {
  customElements.define('momax-journal', class extends HTMLElement {
    connectedCallback() {
      // -- State and variables
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.track = this.querySelector('.momax-journal__track');
      this.cards = [...this.querySelectorAll('[data-journal-card]')];
      this.rail = this.querySelector('.momax-journal__rail');
      this.mobile = matchMedia('(max-width: 740px)');

      // -- Listeners
      this.addEventListener('click', event => {
        const filter = event.target.closest('[data-journal-filter]');
        const direction = event.target.closest('[data-journal-direction]');
        if (filter) this.filter(filter.dataset.journalFilter);
        if (direction) this.move(Number(direction.dataset.journalDirection));
      }, { signal });
      this.track.addEventListener('scroll', () => this.update(), { passive: true, signal });
      this.track.addEventListener('keydown', event => {
        if (event.target !== this.track) return;
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        if (event.key === 'Home' || event.key === 'End') this.scrollTo(event.key === 'Home' ? 0 : this.track.scrollWidth);
        else this.move(event.key === 'ArrowLeft' ? -1 : 1);
      }, { signal });
      this.rail.addEventListener('pointerdown', event => {
        if (this.rail.hidden) return;
        const thumb = this.rail.firstElementChild.getBoundingClientRect();
        this.grabOffset = event.clientX >= thumb.left && event.clientX <= thumb.right ? event.clientX - thumb.left : thumb.width / 2;
        this.dragPointer = event.pointerId;
        this.track.style.scrollSnapType = 'none';
        this.rail.setPointerCapture(event.pointerId);
        this.dragRail(event.clientX);
      }, { signal });
      this.rail.addEventListener('pointermove', event => {
        if (event.pointerId === this.dragPointer) this.dragRail(event.clientX);
      }, { signal });
      const endDrag = event => {
        if (event.pointerId !== this.dragPointer) return;
        this.dragPointer = null;
        this.track.style.scrollSnapType = '';
      };
      this.rail.addEventListener('pointerup', endDrag, { signal });
      this.rail.addEventListener('pointercancel', endDrag, { signal });
      this.rail.addEventListener('keydown', event => {
        const max = this.track.scrollWidth - this.track.clientWidth;
        const step = this.visibleCards()[0]?.getBoundingClientRect().width || this.track.clientWidth;
        let left = this.track.scrollLeft;
        if (event.key === 'ArrowLeft') left -= step;
        else if (event.key === 'ArrowRight') left += step;
        else if (event.key === 'Home') left = 0;
        else if (event.key === 'End') left = max;
        else return;
        event.preventDefault();
        this.scrollTo(left);
      }, { signal });
      this.mobile.addEventListener('change', () => this.reorder(), { signal });
      this.addEventListener('shopify:block:select', event => {
        const card = event.target.closest('[data-journal-card]');
        if (!card) return;
        this.filter('all');
        this.scrollTo(this.position(card));
      }, { signal });
      this.observer = new ResizeObserver(() => this.update());
      this.observer.observe(this.track);
      this.reorder();
    }

    // -- Functions
    visibleCards() {
      return [...this.track.children].filter(card => !card.hidden);
    }

    reorder() {
      // Move the actual nodes so keyboard and screen-reader order match the mobile layout.
      const cards = [...this.cards];
      if (this.mobile.matches) cards.sort((a, b) => Number(a.dataset.mobileOrder) - Number(b.dataset.mobileOrder));
      else cards.sort((a, b) => Number(a.dataset.desktopOrder) - Number(b.dataset.desktopOrder));
      this.track.append(...cards);
      this.track.scrollLeft = 0;
      this.rebuild();
    }

    filter(category) {
      const changed = this.querySelector('[data-journal-filter][aria-pressed="true"]')?.dataset.journalFilter !== category;
      this.querySelectorAll('[data-journal-filter]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.journalFilter === category)));
      this.cards.forEach(card => { card.hidden = category !== 'all' && card.dataset.category !== category; });
      this.track.scrollLeft = 0;
      this.rebuild();
      if (changed) this.dispatchEvent(new CustomEvent('momax:journal:filter-change', { bubbles: true }));
    }

    rebuild() {
      const cards = this.visibleCards();
      this.querySelector('.momax-journal__empty').hidden = cards.length > 0;
      this.update();
    }

    dragRail(clientX) {
      const rail = this.rail.getBoundingClientRect();
      const thumbWidth = this.rail.firstElementChild.getBoundingClientRect().width;
      const travel = rail.width - thumbWidth;
      const max = this.track.scrollWidth - this.track.clientWidth;
      this.track.scrollLeft = travel > 0 ? Math.max(0, Math.min(1, (clientX - rail.left - this.grabOffset) / travel)) * max : 0;
      this.update();
    }

    position(card) {
      return this.track.scrollLeft + card.getBoundingClientRect().left - this.track.getBoundingClientRect().left - parseFloat(getComputedStyle(this.track).paddingLeft);
    }

    scrollTo(left) {
      this.track.scrollTo({ left, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    }

    move(direction) {
      const card = this.visibleCards()[0];
      if (card) this.scrollTo(this.track.scrollLeft + direction * (card.getBoundingClientRect().width + parseFloat(getComputedStyle(this.track).gap)));
    }

    update() {
      const max = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const offset = Math.max(0, Math.min(max, this.track.scrollLeft));
      this.querySelectorAll('[data-journal-direction]').forEach(button => {
        button.disabled = max <= 1 || (Number(button.dataset.journalDirection) < 0 ? offset <= 1 : offset >= max - 1);
      });
      this.rail.hidden = max <= 1;
      const width = this.track.scrollWidth > 0 ? Math.min(100, this.track.clientWidth / this.track.scrollWidth * 100) : 100;
      this.rail.style.setProperty('--journal-thumb-width', `${width}%`);
      this.rail.style.setProperty('--journal-thumb-offset', `${max > 1 ? offset / max * (100 - width) / width * 100 : 0}%`);
      this.rail.setAttribute('aria-valuenow', String(Math.round(max > 1 ? offset / max * 100 : 0)));
    }

    // -- Lifecycle hooks
    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
    }
  });
}
